// frontend/src/pages/Socrates/contexts/TemplateContext.js

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import templateService from '../../../services/socrates/templateService';
import { useSocrates } from './SocratesContext';
import { useUser } from '../../../contexts/UserContext';

const TemplateContext = createContext();

export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
};

export const TemplateProvider = ({ children }) => {
  const { setLoading, setError, enterpriseId, userRole, userData } = useSocrates();
  const [templates, setTemplates] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false); // Track if we've attempted initialization
  
  // Use ref to track if we're currently fetching to prevent race conditions
  const isFetchingRef = useRef(false);

  // Memoized function to load templates from localStorage
  const loadTemplatesFromStorage = useCallback(() => {
    try {
      const storedTemplates = localStorage.getItem('socrates_templates');
      const timestamp = localStorage.getItem('socrates_templates_timestamp');
      
      if (storedTemplates && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        const maxAge = 5 * 60 * 1000; // 5 minutes
        
        if (age < maxAge) {
          const allTemplates = JSON.parse(storedTemplates);
          setTemplates(allTemplates);
          
          const system = allTemplates.filter(t => t.is_system_template === 'true');
          const user = allTemplates.filter(t => t.is_system_template !== 'true');
          
          setSystemTemplates(system);
          setUserTemplates(user);
          setTemplatesLoaded(true);
          setHasInitialized(true);
          
          console.log('Templates loaded from storage:', allTemplates.length);
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading templates from storage:', error);
    }
    return false;
  }, []);

  // Memoized function to fetch templates from API
  const fetchTemplates = useCallback(async (forceRefresh = false) => {
    // Prevent multiple simultaneous calls, but allow force refresh
    if (!forceRefresh && templatesLoaded && isFetchingRef.current) {
      console.log('Templates already loaded or currently fetching, skipping fetch');
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching templates from API...');
      const response = await templateService.getTemplates();
      
      // Handle different response formats
      let allTemplates = [];
      
      if (response.success && response.data?.templates) {
        // Standard success format
        allTemplates = response.data.templates;
      } else if (response.templates) {
        // Direct templates array
        allTemplates = response.templates;
      } else if (typeof response === 'object' && response.body) {
        // Wrapped in body property (sometimes happens with API Gateway)
        try {
          const parsedBody = typeof response.body === 'string' 
            ? JSON.parse(response.body) 
            : response.body;
          
          allTemplates = parsedBody.templates || [];
        } catch (parseError) {
          console.error('Error parsing response body:', parseError);
        }
      }
      
      console.log('Templates fetched successfully:', allTemplates.length);
      
      setTemplates(allTemplates);
      
      // Separate system and user templates
      const system = allTemplates.filter(t => t.is_system_template === 'true');
      const user = allTemplates.filter(t => t.is_system_template !== 'true');
      
      setSystemTemplates(system);
      setUserTemplates(user);
      setTemplatesLoaded(true);
      setHasInitialized(true);
      
      // Store in localStorage for future use
      localStorage.setItem('socrates_templates', JSON.stringify(allTemplates));
      localStorage.setItem('socrates_templates_timestamp', Date.now().toString());
      
      return allTemplates;
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError(`Failed to fetch templates: ${error.message}`);
      // Mark as initialized even on error to prevent infinite retries
      setHasInitialized(true);
      return [];
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [setLoading, setError]); // Remove templatesLoaded from dependencies

  // Initialize templates - try storage first, then API
  const initializeTemplates = useCallback(async () => {
    if (hasInitialized || isFetchingRef.current) {
      console.log('Templates already initialized or currently initializing');
      return;
    }

    console.log('Initializing templates...');
    
    // Try to load from storage first
    const loadedFromStorage = loadTemplatesFromStorage();
    
    // If not loaded from storage, fetch from API
    if (!loadedFromStorage) {
      await fetchTemplates();
    }
  }, [hasInitialized, loadTemplatesFromStorage, fetchTemplates]);

  // Force refresh templates
  const refreshTemplates = useCallback(async () => {
    console.log('Refreshing templates...');
    
    // Clear localStorage cache
    localStorage.removeItem('socrates_templates');
    localStorage.removeItem('socrates_templates_timestamp');
    
    // Reset state but don't set isFetchingRef to false yet
    setTemplatesLoaded(false);
    setHasInitialized(false);
    
    try {
      // Force fetch fresh templates
      const templates = await fetchTemplates(true); // Pass true to force refresh
      console.log('Templates refreshed successfully:', templates.length);
      return true;
    } catch (error) {
      console.error('Error refreshing templates:', error);
      setError(`Failed to refresh templates: ${error.message}`);
      return false;
    }
  }, [fetchTemplates, setError]);

  // Create template
  const createTemplate = useCallback(async (templateData) => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        ...templateData,
        enterprise_id: enterpriseId || userData?.enterprise_id || templateData.enterprise_id,
        user_role: userRole || userData?.role_key
      };
      
      console.log('Creating template with user data:', {
        enterpriseId: payload.enterprise_id,
        userRole: payload.user_role,
        isSystemTemplate: payload.is_system_template
      });

      const response = await templateService.createTemplate(payload);
      
      // Check if the response contains a success message
      if (response.success || 
          (response.message && response.message.toLowerCase().includes('success')) ||
          response.template) {
        console.log('Template created successfully, refreshing templates list');
        const refreshSuccess = await refreshTemplates(); // Refresh after creation
        console.log('Templates refresh after create:', refreshSuccess ? 'successful' : 'failed');
        setIsFormOpen(false);
        return { success: true, data: response };
      } else {
        throw new Error(response.message || 'Failed to create template');
      }
    } catch (error) {
      // Check if the error message actually indicates success
      if (error.message && error.message.toLowerCase().includes('success')) {
        console.log('Template creation reported as error but was successful, refreshing templates');
        await refreshTemplates();
        setIsFormOpen(false);
        return { success: true };
      }
      
      console.error('Error creating template:', error);
      setError(error.message || 'Failed to create template');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, userRole, userData, refreshTemplates, setLoading, setError, setIsFormOpen]);

  // Update template
  const updateTemplate = useCallback(async (templateId, templateData) => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        ...templateData,
        enterprise_id: enterpriseId || userData?.enterprise_id || templateData.enterprise_id,
        user_role: userRole || userData?.role_key
      };
      
      console.log('Updating template...', {
        templateId,
        enterpriseId,
        userRole,
        isSystemTemplate: payload.is_system_template
      });
      
      const response = await templateService.updateTemplate(templateId, payload);
      
      // Check if the response contains a success message or is successful
      if (response.success || 
          (response.message && response.message.toLowerCase().includes('success')) ||
          response.template) {
        console.log('Template updated successfully, refreshing templates list');
        const refreshSuccess = await refreshTemplates(); // Refresh after update
        console.log('Templates refresh after update:', refreshSuccess ? 'successful' : 'failed');
        setIsFormOpen(false);
        setSelectedTemplate(null);
        return { success: true, data: response };
      } else {
        throw new Error(response.message || 'Failed to update template');
      }
    } catch (error) {
      // Check if the error message actually indicates success
      if (error.message && error.message.toLowerCase().includes('success')) {
        console.log('Template update reported as error but was successful, refreshing templates');
        await refreshTemplates();
        setIsFormOpen(false);
        setSelectedTemplate(null);
        return { success: true };
      }
      
      console.error('Error updating template:', error);
      setError(error.message || 'Failed to update template');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, userRole, userData, refreshTemplates, setLoading, setError, setIsFormOpen, setSelectedTemplate]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Build the proper payload structure
      const payload = {
        templateId,
        user_role: userRole || userData?.role_key,
        enterprise_id: enterpriseId || userData?.enterprise_id
      };
  
      console.log('Deleting template with payload:', payload);
      
      const response = await templateService.deleteTemplate(payload);
      
      if (response.success || 
          (response.message && response.message.toLowerCase().includes('success'))) {
        console.log('Template deleted successfully, refreshing templates list');
        const refreshSuccess = await refreshTemplates(); // Refresh after deletion
        console.log('Templates refresh after delete:', refreshSuccess ? 'successful' : 'failed');
        return { success: true, data: response };
      } else {
        throw new Error(response.message || 'Failed to delete template');
      }
    } catch (error) {
      // Check if the error message actually indicates success
      if (error.message && error.message.toLowerCase().includes('success')) {
        console.log('Template deletion reported as error but was successful, refreshing templates');
        await refreshTemplates();
        return { success: true };
      }
      
      console.error('Error deleting template:', error);
      setError(error.message || 'Failed to delete template');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userRole, userData, enterpriseId, refreshTemplates, setLoading, setError]);

  // Form management
  const openCreateForm = useCallback(() => {
    setSelectedTemplate(null);
    setFormMode('create');
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((template) => {
    setSelectedTemplate(template);
    setFormMode('edit');
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setSelectedTemplate(null);
    setFormMode('create');
  }, []);

  const value = {
    templates,
    systemTemplates,
    userTemplates,
    selectedTemplate,
    isFormOpen,
    formMode,
    templatesLoaded,
    hasInitialized,
    initializeTemplates,
    fetchTemplates,
    refreshTemplates,
    loadTemplatesFromStorage,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    openCreateForm,
    openEditForm,
    closeForm,
    setSelectedTemplate
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};